/*
 * annualVisitPlanner.js
 *
 * Recomendador de fecha idónea para la visita anual de conservación preventiva
 * al emplazamiento, basado en el clima de la provincia del site.
 *
 * Criterio: cada zona climática tiene una ventana temporal en la que:
 *   1. el clima es benigno (no llueve, no hay heladas, no hay calor extremo),
 *   2. ha pasado la estación más agresiva (verano/tormentas/salitre),
 *   3. queda margen para reparar antes de la siguiente estación agresiva.
 *
 * De este modo cada visita coincide con el punto óptimo para detectar
 * desperfectos generados por la instalación y actuar sin urgencia.
 */

const CLIMATE_ZONES = [
  {
    id: 'norte_atlantico',
    label: 'Atlántico húmedo',
    month: 6,   // junio
    day: 10,
    rationale: 'Junio marca el final del ciclo lluvioso atlántico y precede a las tormentas de otoño. Es la ventana más seca del norte para inspeccionar cubiertas, impermeabilizaciones y anclajes tras un año de humedad acumulada.',
    provinces: [
      'Galicia', 'A Coruña', 'La Coruña', 'Lugo', 'Ourense', 'Orense', 'Pontevedra',
      'Asturias',
      'Cantabria',
      'Vizcaya', 'Bizkaia', 'Guipúzcoa', 'Gipuzkoa', 'Álava', 'Araba',
      'Navarra',
    ],
  },
  {
    id: 'mediterraneo',
    label: 'Mediterráneo',
    month: 10,  // octubre
    day: 15,
    rationale: 'Octubre llega tras el pico térmico del verano y antes de las tormentas de gota fría. Es el momento óptimo para detectar el desgaste inducido por julio y agosto, y reforzar el emplazamiento antes de las lluvias intensas del otoño.',
    provinces: [
      'Barcelona', 'Girona', 'Gerona', 'Tarragona', 'Lleida', 'Lérida',
      'Valencia', 'Alicante', 'Castellón', 'Castelló',
      'Murcia',
      'Almería', 'Málaga',
      'Baleares', 'Illes Balears', 'Islas Baleares', 'Mallorca', 'Menorca', 'Ibiza',
    ],
  },
  {
    id: 'continental',
    label: 'Continental interior',
    month: 10,  // octubre
    day: 5,
    rationale: 'A primeros de octubre ya ha pasado el pico de calor y las granizadas estivales, y queda margen suficiente antes de las heladas nocturnas. Ventana idónea para revisar impermeabilización, sellados y ensamblajes.',
    provinces: [
      'Madrid',
      'Toledo', 'Cuenca', 'Guadalajara', 'Ciudad Real', 'Albacete',
      'Ávila', 'Avila', 'Segovia', 'Soria', 'Burgos', 'Palencia',
      'Zamora', 'Salamanca', 'Valladolid', 'León',
      'Zaragoza', 'Teruel', 'Huesca',
      'La Rioja', 'Rioja',
    ],
  },
  {
    id: 'sur_calido',
    label: 'Cálido seco',
    month: 11,  // noviembre
    day: 5,
    rationale: 'Noviembre queda fuera de la ola de calor persistente que se prolonga hasta septiembre y la cubierta ya ha enfriado. Permite intervenciones largas y minuciosas sin riesgo térmico para el equipo técnico.',
    provinces: [
      'Sevilla', 'Córdoba', 'Cordoba', 'Cádiz', 'Cadiz', 'Huelva',
      'Jaén', 'Jaen', 'Granada',
      'Badajoz', 'Cáceres', 'Caceres',
    ],
  },
  {
    id: 'canarias',
    label: 'Subtropical',
    month: 3,   // marzo
    day: 15,
    rationale: 'Marzo se sitúa antes del pico solar y de la calima estival. El salitre marino y la radiación UV han acumulado impacto sobre estructuras galvanizadas y tornillería, y las temperaturas suaves del mes permiten actuar con tiempo y calidad.',
    provinces: [
      'Las Palmas', 'Santa Cruz de Tenerife', 'Tenerife', 'Canarias',
      'Gran Canaria', 'Lanzarote', 'Fuerteventura', 'La Palma', 'La Gomera', 'El Hierro',
    ],
  },
];

// Zona por defecto: mediterráneo (ancho amplio y mes bueno para el 60% del país).
const DEFAULT_ZONE = CLIMATE_ZONES[1];

const normalize = (s) => (s || '')
  .toString().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

/**
 * Devuelve la zona climática que corresponde a la provincia dada.
 * Si no la reconoce, devuelve la zona por defecto (mediterráneo).
 */
export function zoneForProvince(province) {
  const n = normalize(province);
  if (!n) return DEFAULT_ZONE;
  for (const z of CLIMATE_ZONES) {
    if (z.provinces.some((p) => normalize(p) === n || n.includes(normalize(p)))) {
      return z;
    }
  }
  return DEFAULT_ZONE;
}

/**
 * Calcula la próxima ocurrencia de la fecha idónea (año actual si aún no pasó,
 * o año siguiente si ya pasó). Devuelve un Date UTC.
 */
export function nextVisitDate(zone, from = new Date()) {
  const now = new Date(from);
  let year = now.getUTCFullYear();
  const target = new Date(Date.UTC(year, zone.month - 1, zone.day));
  if (target < now) {
    return new Date(Date.UTC(year + 1, zone.month - 1, zone.day));
  }
  return target;
}

/**
 * Wrapper que devuelve todo lo necesario para pintar en UI.
 */
export function suggestVisit(province, from = new Date()) {
  const zone = zoneForProvince(province);
  const date = nextVisitDate(zone, from);
  return {
    zone,
    date,
    dateLabel: date.toLocaleDateString('es-ES', { day: '2-digit', month: 'long', year: 'numeric' }),
    monthLabel: date.toLocaleDateString('es-ES', { month: 'long' }),
    daysFromNow: Math.round((date.getTime() - from.getTime()) / (24 * 60 * 60 * 1000)),
  };
}
